import _styled, { default as _styled2, default as _styled3 } from "styled-components";
const _excluded = ["disabled", "selected", "defaultSelected", "onChange", "onClose", "children", "className", "style"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import CloseButton from '@adsk/alloy-react-close-button';
import { useControlled, useInstanceId } from '@adsk/alloy-react-helpers';
import theme, { stylePropType, getInteractiveStyles } from '@adsk/alloy-react-theme';
/**
 * Tags are compact elements that can be used to represent small blocks of information.
 * They are descriptive words to either filter or identify the content, and can also be a good alternative to toggle switches.
 */
const Tag = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      disabled = false,
      selected: selectedProp,
      defaultSelected = false,
      onChange: onChangeProp,
      onClose,
      children,
      className,
      style
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const closeRef = useRef(null);
  const id = `alloy-tag-${useInstanceId()}`;
  const [selected, setSelected] = useControlled(defaultSelected !== null && defaultSelected !== void 0 ? defaultSelected : false, selectedProp);
  const onChange = useCallback(event => {
    if (!disabled) {
      onChangeProp && onChangeProp(event);
      setSelected(!selected);
    }
  }, [disabled, onChangeProp, selected, setSelected]);
  const onKeyDown = useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      const input = document.getElementById(id);
      input.click();
    } else if (event.key === 'Backspace' || event.key === 'Delete') {
      var _closeRef$current;
      (_closeRef$current = closeRef.current) === null || _closeRef$current === void 0 ? void 0 : _closeRef$current.click();
    }
  }, [id]);
  return (
    /*#__PURE__*/
    // Will be fixed in https://jira.autodesk.com/browse/DSGN-1839
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    React.createElement(_StyledLabel, _extends({
      // Will be fixed in https://jira.autodesk.com/browse/DSGN-1839
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      className: className,
      htmlFor: id,
      onKeyDown: onKeyDown
    }, props, {
      $_css: [_objectSpread(_objectSpread(_objectSpread(_objectSpread({
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: `0px ${theme.spacing.XS}px`,
        background: theme.tokens.colors.background.tertiary.value,
        color: disabled ? theme.tokens.colors.text.inactive.value : theme.tokens.colors.text.primary.value,
        borderRadius: theme.borderRadius.pill,
        border: `1px solid ${theme.tokens.colors.border.value}`
      }, getInteractiveStyles(disabled)), !disabled && {
        ':hover': {
          borderColor: theme.tokens.colors.border.hover.value
        },
        ':active': {
          borderColor: theme.tokens.colors.border.pressed.value
        },
        ':focus': {
          borderColor: theme.tokens.colors.border.neutral.focused.value
        }
      }), selected && {
        background: theme.tokens.colors.background.selected.subtle.value,
        borderColor: theme.tokens.colors.border.selected.subtle.value
      }), disabled && {
        background: selected ? theme.tokens.colors.background.selected.subtle.value : theme.tokens.colors.background.neutral.inactive.value,
        borderColor: selected ? theme.tokens.colors.border.selected.subtle.value : theme.tokens.colors.border.inactive.value
      }), theme.typography.bodyMediumBold, style]
    }), /*#__PURE__*/React.createElement(_StyledInput, {
      id: id,
      ref: ref,
      value: selected.toString(),
      onChange: onChange,
      type: "checkbox",
      checked: selected,
      disabled: disabled,
      $_css2: [{
        display: 'none'
      }]
    }), /*#__PURE__*/React.createElement(_StyledSpan, {
      $_css3: `${theme.spacing.XXS}px 0px`
    }, children), onClose && /*#__PURE__*/React.createElement(CloseButton, {
      ref: closeRef,
      tabIndex: -1,
      disabled: disabled,
      onClick: e => {
        e.stopPropagation(); // prevent Tag selection
        onClose(e);
      }
    }))
  );
});
Tag.displayName = 'Tag';
Tag.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  Tag text content. */
  children: PropTypes.any,
  /** Whether Tag is disabled */
  disabled: PropTypes.bool,
  /** Whether Tag is selected when first rendered */
  defaultSelected: PropTypes.bool,
  /** Whether Tag is selected */
  selected: PropTypes.bool,
  /** Function executed upon (un)selecting tag */
  onChange: PropTypes.func,
  /** Function executed upon closing tag */
  onClose: PropTypes.func
};
export default Tag;
var _StyledLabel = _styled("label")`${p => p.$_css}`;
var _StyledInput = _styled2("input")`${p => p.$_css2}`;
var _StyledSpan = _styled3("span").withConfig({
  displayName: "Tag___StyledSpan",
  componentId: "sc-mzfz8r-0"
})(p => ({
  cursor: 'inherit',
  padding: p.$_css3
}));
//# sourceMappingURL=Tag.js.map